import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { useGetRolesQuery } from '../../../store/api/roleApi';
import { useCreateUserMutation, useUpdateUserMutation } from '../../../store/api/userApi';

const initialFormData = {
  username: '',
  email: '',
  firstName: '',
  lastName: '',
  roleId: '',
  password: '',
};

function UserForm({
  user = null,
  isEdit = false,
  loading = false,
  compact = false,
  onCancel,
  onSuccess,
}) {
  const { data: roles } = useGetRolesQuery();
  const [createUser] = useCreateUserMutation();
  const [updateUser] = useUpdateUserMutation();
  const [formData, setFormData] = useState(initialFormData);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    if (user) {
      setFormData({
        username: user.username || '',
        email: user.email || '',
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        roleId: user.role?.id || user.roleId || '',
        password: '',
      });
    } else if (!isEdit) {
      setFormData(initialFormData);
    }
  }, [user, isEdit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setSubmitError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    try {
      if (!formData.roleId) {
        setSubmitError('Role is required');
        return;
      }
      if (!isEdit && !formData.password) {
        setSubmitError('Password is required for new users');
        return;
      }

      const payload = {
        username: formData.username,
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        roleId: formData.roleId,
      };
      if (formData.password) {
        payload.password = formData.password;
      }

      if (isEdit) {
        await updateUser({ id: user.id, ...payload }).unwrap();
      } else {
        await createUser(payload).unwrap();
      }
      onSuccess?.();
    } catch (err) {
      setSubmitError(err?.data?.message || 'Error saving user');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            label="Username"
            name="username"
            value={formData.username}
            onChange={handleChange}
            required
            disabled={isEdit}
            size={compact ? 'small' : 'medium'}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            required
            size={compact ? 'small' : 'medium'}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            label="First Name"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            required
            size={compact ? 'small' : 'medium'}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            label="Last Name"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            required
            size={compact ? 'small' : 'medium'}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            select
            label="Role"
            name="roleId"
            value={formData.roleId}
            onChange={handleChange}
            required
            size={compact ? 'small' : 'medium'}
          >
            <MenuItem value="">Select Role</MenuItem>
            {roles?.map((role) => (
              <MenuItem key={role.id} value={role.id}>
                {role.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            label={isEdit ? 'New Password (optional)' : 'Password'}
            name="password"
            type="password"
            value={formData.password}
            onChange={handleChange}
            size={compact ? 'small' : 'medium'}
          />
        </Grid>
        {submitError && (
          <Grid size={{ xs: 12 }}>
            <Typography variant="body2" color="error">
              {submitError}
            </Typography>
          </Grid>
        )}
        <Grid size={{ xs: 12 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button type="button" variant="outlined" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" variant="contained">
              {isEdit ? 'Update' : 'Create'}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </form>
  );
}

export default UserForm;

